import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Button,
  TextField,
  Divider,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Input
} from '@mui/material';
import {
  Edit,
  Save,
  Cancel,
  Person,
  Email,
  Phone,
  Badge,
  AdminPanelSettings,
  LocalHospital,
  DateRange,
  CloudUpload,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { ProfilePictureUpload } from '../../components/Profile/ProfilePictureUpload';

export default function ProfilePage() {
  const { user, isLoading, updateUser, token } = useAuth();
  const [isEditing, setIsEditing] = useState(false);

  const [editData, setEditData] = useState<{
    username: string;
    phoneNumber: string;
    specialization: string;
    department: string;
    license_number: string;
    years_of_experience: string;
    hospital_name: string;
    location: string;
    profile_image: string | File;
  }>({
    username: user?.username || '',
    phoneNumber: user?.phoneNumber || '',
    specialization: user?.specialization || '',
    department: user?.department || '',
    license_number: user?.license_number || '',
    years_of_experience: user?.years_of_experience?.toString() || '',
    hospital_name: user?.hospital_name || '',
    location: user?.location || '',
    profile_image: user?.profile_image || ''
  });

  const [formErrors, setFormErrors] = useState({
    username: '',
    phoneNumber: '',
    specialization: '',
    department: '',
    license_number: '',
    years_of_experience: '',
    hospital_name: '',
    location: '',
    profile_image: ''
  });

  // Fetch latest user data on mount
  useEffect(() => {
    const fetchUserData = async () => {
      if (!token) return;
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/users/me`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          let fetchedUser = await response.json();
          console.log('Fetched base user data on mount:', fetchedUser);

          // Fetch doctor-specific data if role is doctor
          if (fetchedUser.role === 'doctor' && fetchedUser.id) {
            try {
              const doctorResponse = await fetch(`${process.env.REACT_APP_API_URL}/auth/users/${fetchedUser.id}/doctor-info`, {
                headers: {
                  'Authorization': `Bearer ${token}`
                }
              });

              if (doctorResponse.ok) {
                const doctorInfo = await doctorResponse.json();
                console.log('Fetched doctor-info on mount:', doctorInfo);
                // Merge doctor info with base user data
                fetchedUser = {
                  ...fetchedUser,
                  ...doctorInfo
                };
                console.log('Merged user data:', fetchedUser);
              }
            } catch (error) {
              console.error('Failed to fetch doctor info:', error);
              // Continue with base user data if doctor-info fetch fails
            }
          }

          updateUser(fetchedUser);
        }
      } catch (error) {
        console.error('Failed to fetch user data:', error);
      }
    };

    fetchUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Sync editData with user changes (important when fetch completes)
  useEffect(() => {
    if (user && !isEditing) {
      setEditData({
        username: user.username || '',
        phoneNumber: user.phoneNumber || '',
        specialization: user.specialization || '',
        department: user.department || '',
        license_number: user.license_number || '',
        years_of_experience: user.years_of_experience?.toString() || '',
        hospital_name: user.hospital_name || '',
        location: user.location || '',
        profile_image: user.profile_image || ''
      });
    }
  }, [user, isEditing]);

  // Cleanup object URLs when component unmounts or user changes
  React.useEffect(() => {
    return () => {
      if (editData.profile_image instanceof File) {
        URL.revokeObjectURL(getProfilePicturePreview(editData.profile_image));
      }
    };
  }, [editData.profile_image]);

  const [saveLoading, setSaveLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [imageTimestamp, setImageTimestamp] = useState(Date.now());

  // Utility function to fix S3 URL format
  const fixS3Url = (url: string | undefined): string | undefined => {
    if (!url) return undefined;

    // Check if it's an S3 URL with regional endpoint
    const s3RegionalPattern = /https:\/\/([^.]+)\.s3\.([^.]+)\.amazonaws\.com\/(.+)/;
    const match = url.match(s3RegionalPattern);

    if (match) {
      const bucketName = match[1];
      const region = match[2];
      const path = match[3];

      // Convert to path-style URL (works better with CORS)
      // OR use virtual-hosted style without region
      const fixedUrl = `https://${bucketName}.s3.amazonaws.com/${path}`;
      // console.log('🔧 Fixed S3 URL:', url, '->', fixedUrl);
      return fixedUrl;
    }

    return url;
  };

  // Utility function to get cache-busted image URL
  const getCacheBustedImageUrl = (url: string | undefined, timestamp?: number): string | undefined => {
    if (!url) return undefined;

    const fixedUrl = fixS3Url(url);
    if (!fixedUrl) return undefined;

    const ts = timestamp || Date.now();
    return `${fixedUrl}?t=${ts}`;
  };

  if (isLoading || !user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const validateField = (name: string, value: string): string => {
    switch (name) {
      case 'username':
        if (!value.trim()) return 'Name is required';
        if (value.trim().length < 3) return 'Name must be at least 3 characters';
        if (!/^[a-zA-Z\s.]+$/.test(value)) return 'Name can only contain letters, spaces, and dots';
        return '';

      case 'phoneNumber':
        if (!value.trim()) return 'Phone number is required';
        const cleanPhone = value.replace(/[\s()-]/g, '');
        if (!/^[6-9]\d{9}$/.test(cleanPhone)) return 'Invalid Indian phone number. Must be 10 digits starting with 6-9';
        return '';

      case 'specialization':
        if (user.role === 'doctor') {
          if (!value.trim()) return 'Specialization is required';
          if (value.trim().length < 3) return 'Specialization must be at least 3 characters';
        }
        return '';

      case 'department':
        if (value && value.trim().length < 3) return 'Department must be at least 3 characters';
        return '';

      case 'license_number':
        if (user.role === 'doctor') {
          if (!value.trim()) return 'License number is required';
          if (value.trim().length < 5) return 'License number must be at least 5 characters';
        }
        return '';

      case 'years_of_experience':
        if (user.role === 'doctor') {
          if (!value) return 'Years of experience is required';
          const years = parseInt(value);
          if (isNaN(years)) return 'Must be a valid number';
          if (years < 0) return 'Cannot be negative';
          if (years > 60) return 'Years of experience cannot exceed 60';
        }
        return '';

      default:
        return '';
    }
  };

  const handleProfilePictureChange = async (file: File | null) => {
    if (!file) return;

    // Store the file for later upload during save
    setEditData({ ...editData, profile_image: file as File });
    setFormErrors({ ...formErrors, profile_image: '' });

    // Automatically upload the image immediately after selection
    await uploadProfilePicture(file);
  };

  const handleDeleteProfilePicture = async () => {
    // Automatically upload the deletion (send empty profile picture)
    await uploadProfilePicture(null);
  };

  // Helper function to get preview URL for File objects (only for edit preview use internally, UI uses user.profile_image)
  const getProfilePicturePreview = (picture: string | File | null) => {
    if (!picture) return '';
    if (picture instanceof File) {
      return URL.createObjectURL(picture);
    }
    return picture;
  };

  // Function to upload profile picture immediately
  const uploadProfilePicture = async (file: File | null) => {
    if (!user) return;

    setSaveLoading(true);
    setErrorMessage('');

    try {
      const API_URL = process.env.REACT_APP_API_URL;

      // Determine the correct endpoint based on user role
      let endpoint = '';
      let formData: FormData | undefined;
      let requestBody: any;

      if (user.role === 'doctor') {
        if (file) {
          // Use profile picture upload endpoint for doctors with images
          endpoint = `${API_URL}/auth/users/${user.id}/profile-picture`;
          formData = new FormData();
          formData.append('file', file);
          // Add all required fields as JSON for doctors
          const doctorData = {
            username: editData.username,
            full_name: editData.username,
            phoneNumber: editData.phoneNumber,
            specialization: editData.specialization,
            department: editData.department || '',
            license_number: editData.license_number,
            years_of_experience: editData.years_of_experience,
            hospital_name: editData.hospital_name || '',
            location: editData.location || ''
          };
          console.log('📤 Doctor data being sent:', doctorData);
          formData.append('data', JSON.stringify(doctorData));
        } else {
          // Use regular doctor-info endpoint for removing profile picture
          endpoint = `${API_URL}/auth/users/${user.id}/doctor-info`;
          requestBody = {
            username: user.username,
            full_name: user.full_name || user.username,
            phoneNumber: user?.phoneNumber || '',
            specialization: user?.specialization || '',
            department: user?.department || '',
            license_number: user?.license_number || '',
            years_of_experience: user?.years_of_experience?.toString() || '',
            hospital_name: user?.hospital_name || '',
            location: user?.location || '',
            profile_image: ''
          };
        }
      } else if (user.role === 'marketinguser') {
        if (file) {
          // Use profile picture upload endpoint for marketing users with images
          endpoint = `${API_URL}/auth/users/${user.id}/profile-picture`;
          formData = new FormData();
          formData.append('file', file);
          // Add all required fields as JSON for marketing users
          const marketingData = {
            username: editData.username,
            full_name: editData.username,
            phoneNumber: editData.phoneNumber,
            hospital_name: editData.hospital_name || '',
            location: editData.location || ''
          };
          console.log('📤 Marketing user data being sent:', marketingData);
          formData.append('data', JSON.stringify(marketingData));
        } else {
          // Use regular marketinguser-info endpoint for removing profile picture
          endpoint = `${API_URL}/auth/users/${user.id}/marketinguser-info`;
          requestBody = {
            username: user.username,
            full_name: user.full_name || user.username,
            phoneNumber: user?.phoneNumber || '',
            hospital_name: user?.hospital_name || '',
            location: user?.location || '',
            profile_image: ''
          };
        }
      } else if (user.role === 'superadmin') {
        if (file) {
          // Use profile picture upload endpoint for super admins with images
          endpoint = `${API_URL}/auth/users/${user.id}/profile-picture`;
          formData = new FormData();
          formData.append('file', file);
          // Add all required fields as JSON for super admins
          const superAdminData = {
            username: editData.username,
            full_name: editData.username,
            phoneNumber: editData.phoneNumber,
            hospital_name: editData.hospital_name || '',
            location: editData.location || ''
          };
          console.log('📤 Super admin data being sent:', superAdminData);
          formData.append('data', JSON.stringify(superAdminData));
        } else {
          // Use regular superadmin-info endpoint for removing profile picture
          endpoint = `${API_URL}/auth/users/${user.id}/superadmin-info`;
          requestBody = {
            username: user.username,
            full_name: user.full_name || user.username,
            phoneNumber: user?.phoneNumber || '',
            hospital_name: user?.hospital_name || '',
            location: user?.location || '',
            profile_image: ''
          };
        }
      }

      if (!endpoint) return;

      console.log('🔄 Uploading profile picture...');
      console.log('📍 Endpoint:', endpoint);
      console.log('👤 User ID:', user.id);
      console.log('📁 File to upload:', file ? file.name : 'Removing picture');

      let response: Response | undefined;
      if (file && formData) {
        // Debug: Log FormData contents
        console.log('📤 FormData contents:');
        formData.forEach((value, key) => {
          console.log(`  ${key}:`, value);
        });

        // Use FormData for file upload
        response = await fetch(endpoint, {
          method: 'PUT',
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: formData,
        });
      } else if (!file && requestBody) {
        // Use JSON for removing profile picture - send empty profile_image
        response = await fetch(endpoint, {
          method: 'PUT',
          headers: {
            'accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(requestBody),
        });
      }

      if (!response) {
        setErrorMessage('Failed to upload profile picture: No response received');
        return;
      }

      console.log('📡 Response status:', response.status);

      if (response.ok) {
        const updatedUser = await response.json();
        // console.log('🎯 Updated user data:', updatedUser);
        // console.log('🖼️ Profile image URL:', updatedUser.profile_image);

        // Merge updated data with existing user data to preserve all fields
        const mergedUser = {
          ...user,
          ...updatedUser
        };
        // console.log('📦 Merged user data:', mergedUser);

        // 1. Update Local Storage with merged data
        localStorage.setItem('user', JSON.stringify(mergedUser));

        // 2. Update Edit Data with ALL fields from the API response
        // IMPORTANT: Only update fields that are actually returned by the API
        // The /doctor-info endpoint doesn't return username/phoneNumber, so we preserve existing values
        setEditData({
          username: updatedUser.username !== undefined ? updatedUser.username : (mergedUser.username || editData.username || ''),
          phoneNumber: updatedUser.phoneNumber !== undefined ? updatedUser.phoneNumber : (mergedUser.phoneNumber || editData.phoneNumber || ''),
          specialization: updatedUser.specialization !== undefined ? updatedUser.specialization : (mergedUser.specialization || editData.specialization || ''),
          department: updatedUser.department !== undefined ? updatedUser.department : (mergedUser.department || editData.department || ''),
          license_number: updatedUser.license_number !== undefined ? updatedUser.license_number : (mergedUser.license_number || editData.license_number || ''),
          years_of_experience: updatedUser.years_of_experience !== undefined ? updatedUser.years_of_experience?.toString() : (mergedUser.years_of_experience?.toString() || editData.years_of_experience || ''),
          hospital_name: updatedUser.hospital_name !== undefined ? updatedUser.hospital_name : (mergedUser.hospital_name || editData.hospital_name || ''),
          location: updatedUser.location !== undefined ? updatedUser.location : (mergedUser.location || editData.location || ''),
          profile_image: updatedUser.profile_image !== undefined ? updatedUser.profile_image : (mergedUser.profile_image || editData.profile_image || '')
        });

        // 3. Update User Context with merged data
        updateUser(mergedUser);

        // 4. Update image timestamp to force re-render
        setImageTimestamp(Date.now());

        if (file) {
          setSuccessMessage('Profile picture updated successfully!');
        } else {
          setSuccessMessage('Profile picture removed successfully!');
        }
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        let errorDetail = 'Failed to update profile picture';
        try {
          const error = await response.json();
          console.log('❌ Error response:', error);
          // Handle both string and object error responses
          if (typeof error === 'string') {
            errorDetail = error;
          } else if (error && typeof error.detail === 'string') {
            errorDetail = error.detail;
          } else if (error && typeof error === 'object') {
            // Convert object to string for display
            errorDetail = JSON.stringify(error);
          }
        } catch (parseError) {
          console.log('❌ Failed to parse error response:', parseError);
        }
        setErrorMessage(errorDetail);
      }
    } catch (error) {
      console.log('💥 Exception caught:', error);
      setErrorMessage('Failed to update profile picture. Please try again.');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleFieldChange = (field: string, value: string) => {
    setEditData({ ...editData, [field]: value });
    const error = validateField(field, value);
    setFormErrors({ ...formErrors, [field]: error });
  };

  const validateForm = (): boolean => {
    const errors = {
      username: validateField('username', editData.username),
      phoneNumber: validateField('phoneNumber', editData.phoneNumber),
      specialization: user.role === 'doctor' ? validateField('specialization', editData.specialization) : '',
      department: user.role === 'doctor' ? validateField('department', editData.department) : '',
      license_number: user.role === 'doctor' ? validateField('license_number', editData.license_number) : '',
      years_of_experience: user.role === 'doctor' ? validateField('years_of_experience', editData.years_of_experience) : '',
      hospital_name: '',
      location: '',
      profile_image: ''
    };

    setFormErrors(errors);
    return !Object.values(errors).some(error => error !== '');
  };

  const handleEdit = () => {
    setEditData({
      username: user.username || '',
      phoneNumber: user?.phoneNumber || '',
      specialization: user?.specialization || '',
      department: user?.department || '',
      license_number: user?.license_number || '',
      years_of_experience: user?.years_of_experience?.toString() || '',
      hospital_name: user?.hospital_name || '',
      location: user?.location || '',
      profile_image: user?.profile_image || '' as string | File
    });
    setFormErrors({
      username: '',
      phoneNumber: '',
      specialization: '',
      department: '',
      license_number: '',
      years_of_experience: '',
      hospital_name: '',
      location: '',
      profile_image: ''
    });
    setIsEditing(true);
    setSuccessMessage('');
    setErrorMessage('');
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditData({
      username: user.username || '',
      phoneNumber: user?.phoneNumber || '',
      specialization: user?.specialization || '',
      department: user?.department || '',
      license_number: user?.license_number || '',
      years_of_experience: user?.years_of_experience?.toString() || '',
      hospital_name: user?.hospital_name || '',
      location: user?.location || '',
      profile_image: user?.profile_image || '' as string | File
    });
    setFormErrors({
      username: '',
      phoneNumber: '',
      specialization: '',
      department: '',
      license_number: '',
      years_of_experience: '',
      hospital_name: '',
      location: '',
      profile_image: ''
    });
    setErrorMessage('');
  };

  const handleSave = async () => {
    // Validate form before submitting
    if (!validateForm()) {
      setErrorMessage('Please fix all validation errors before saving');
      return;
    }

    setSaveLoading(true);
    setErrorMessage('');

    try {
      const API_URL = process.env.REACT_APP_API_URL;

      // Determine the correct endpoint based on user role
      let endpoint = '';
      let formData: FormData | undefined;
      let requestBody: any;

      if (user.role === 'doctor') {
        const hasFile = editData.profile_image instanceof File;

        if (hasFile) {
          // Use profile picture upload endpoint for doctors with images
          endpoint = `${API_URL}/auth/users/${user.id}/profile-picture`;
          formData = new FormData();
          formData.append('file', editData.profile_image);
          // Add all required fields as JSON for doctors
          const doctorData = {
            username: editData.username,
            full_name: editData.username,
            phoneNumber: editData.phoneNumber,
            specialization: editData.specialization,
            department: editData.department || '',
            license_number: editData.license_number,
            years_of_experience: parseInt(editData.years_of_experience) || 0,
            hospital_name: editData.hospital_name || '',
            location: editData.location || ''
          };
          console.log('📤 Doctor data being sent in handleSave (FormData):', doctorData);
          formData.append('data', JSON.stringify(doctorData));
        } else {
          // Use regular doctor-info endpoint for saving without images
          endpoint = `${API_URL}/auth/users/${user.id}/doctor-info`;
          requestBody = {
            username: editData.username,
            full_name: editData.username,
            phoneNumber: editData.phoneNumber,
            specialization: editData.specialization,
            department: editData.department || '',
            license_number: editData.license_number,
            years_of_experience: parseInt(editData.years_of_experience) || 0,
            hospital_name: editData.hospital_name || '',
            location: editData.location || '',
            profile_image: editData.profile_image || ''
          };
          console.log('📤 Request body being sent (JSON):', requestBody);
        }
      } else if (user.role === 'marketinguser') {
        const hasFile = editData.profile_image instanceof File;

        if (hasFile) {
          // Use profile picture upload endpoint for marketing users with images
          endpoint = `${API_URL}/auth/users/${user.id}/profile-picture`;
          formData = new FormData();
          formData.append('file', editData.profile_image);
          // Add all required fields as JSON for marketing users
          const marketingData = {
            username: editData.username,
            full_name: editData.username,
            phoneNumber: editData.phoneNumber,
            hospital_name: editData.hospital_name || '',
            location: editData.location || ''
          };
          console.log('📤 Marketing user data being sent in handleSave (FormData):', marketingData);
          formData.append('data', JSON.stringify(marketingData));
        } else {
          // Use regular marketinguser-info endpoint for saving without images
          endpoint = `${API_URL}/auth/users/${user.id}/marketinguser-info`;
          requestBody = {
            username: editData.username,
            full_name: editData.username,
            phoneNumber: editData.phoneNumber,
            hospital_name: editData.hospital_name || '',
            location: editData.location || '',
            profile_image: editData.profile_image || ''
          };
          console.log('📤 Request body being sent (JSON):', requestBody);
        }
      } else if (user.role === 'superadmin') {
        const hasFile = editData.profile_image instanceof File;

        if (hasFile) {
          // Use profile picture upload endpoint for super admins with images
          endpoint = `${API_URL}/auth/users/${user.id}/profile-picture`;
          formData = new FormData();
          formData.append('file', editData.profile_image);
          // Add all required fields as JSON for super admins
          const superAdminData = {
            username: editData.username,
            full_name: editData.username,
            phoneNumber: editData.phoneNumber,
            hospital_name: editData.hospital_name || '',
            location: editData.location || ''
          };
          console.log('📤 Super admin data being sent in handleSave (FormData):', superAdminData);
          formData.append('data', JSON.stringify(superAdminData));
        } else {
          // Use regular superadmin-info endpoint for saving without images
          endpoint = `${API_URL}/auth/users/${user.id}/superadmin-info`;
          requestBody = {
            username: editData.username,
            full_name: editData.username,
            phoneNumber: editData.phoneNumber,
            hospital_name: editData.hospital_name || '',
            location: editData.location || '',
            profile_image: editData.profile_image || ''
          };
          console.log('📤 Request body being sent (JSON):', requestBody);
        }
      }

      if (!endpoint) {
        console.error('❌ No endpoint defined!');
        setErrorMessage('Failed to save profile: No endpoint defined');
        return;
      }

      console.log('💾 Saving profile...');
      console.log('📍 Endpoint:', endpoint);
      console.log('👤 User ID:', user.id);
      console.log('📝 Current editData:', editData);

      // Check if we have a file to upload
      const hasFile = editData.profile_image && editData.profile_image instanceof File;
      console.log('📁 Has file to upload:', hasFile);

      let response: Response | undefined;
      if (hasFile && formData) {
        // Debug: Log FormData contents
        console.log('📤 FormData contents in handleSave:');
        formData.forEach((value, key) => {
          console.log(`  ${key}:`, value);
        });

        // Use FormData for file upload
        response = await fetch(endpoint, {
          method: 'PUT',
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: formData,
        });
      } else if (!hasFile && requestBody) {
        // Use JSON for regular updates (only when no file is being uploaded)
        console.log('📤 JSON request body:', requestBody);

        response = await fetch(endpoint, {
          method: 'PUT',
          headers: {
            'accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(requestBody),
        });
      }

      if (!response) {
        setErrorMessage('Failed to save profile: No response received');
        return;
      }

      console.log('📡 Response status:', response.status);

      if (response.ok) {
        const updatedUser = await response.json();
        console.log('✅ Save successful!');
        console.log('🎯 Updated user data from API:', updatedUser);
        console.log('� Updated fields:', {
          username: updatedUser.username,
          phoneNumber: updatedUser.phoneNumber,
          specialization: updatedUser.specialization,
          department: updatedUser.department,
          hospital_name: updatedUser.hospital_name,
          license_number: updatedUser.license_number,
          years_of_experience: updatedUser.years_of_experience,
          location: updatedUser.location,
          profile_image: updatedUser.profile_image
        });

        // Merge updated data with existing user data
        const mergedUser = {
          ...user,
          ...updatedUser
        };
        console.log('📦 Merged user data:', mergedUser);

        localStorage.setItem('user', JSON.stringify(mergedUser));

        // Update edit data with ALL fields from the API response
        // IMPORTANT: Only update fields that are actually returned by the API
        // The /doctor-info endpoint doesn't return username/phoneNumber, so we preserve existing values
        const newEditData = {
          username: updatedUser.username !== undefined ? updatedUser.username : (mergedUser.username || editData.username || ''),
          phoneNumber: updatedUser.phoneNumber !== undefined ? updatedUser.phoneNumber : (mergedUser.phoneNumber || editData.phoneNumber || ''),
          specialization: updatedUser.specialization !== undefined ? updatedUser.specialization : (mergedUser.specialization || editData.specialization || ''),
          department: updatedUser.department !== undefined ? updatedUser.department : (mergedUser.department || editData.department || ''),
          license_number: updatedUser.license_number !== undefined ? updatedUser.license_number : (mergedUser.license_number || editData.license_number || ''),
          years_of_experience: updatedUser.years_of_experience !== undefined ? updatedUser.years_of_experience?.toString() : (mergedUser.years_of_experience?.toString() || editData.years_of_experience || ''),
          hospital_name: updatedUser.hospital_name !== undefined ? updatedUser.hospital_name : (mergedUser.hospital_name || editData.hospital_name || ''),
          location: updatedUser.location !== undefined ? updatedUser.location : (mergedUser.location || editData.location || ''),
          profile_image: updatedUser.profile_image !== undefined ? updatedUser.profile_image : (mergedUser.profile_image || editData.profile_image || '')
        };
        console.log('🔄 Setting new editData:', newEditData);
        setEditData(newEditData);

        // Consolidated User Context Update with merged data
        console.log('🔄 Updating user context with merged data');
        updateUser(mergedUser);

        // Update image timestamp to force re-render
        setImageTimestamp(Date.now());

        setIsEditing(false);
        setSuccessMessage('Profile updated successfully!');

        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        let errorDetail = 'Failed to update profile';
        try {
          const error = await response.json();
          console.log('❌ Error response:', error);
          // Handle both string and object error responses
          if (typeof error === 'string') {
            errorDetail = error;
          } else if (error && typeof error.detail === 'string') {
            errorDetail = error.detail;
          } else if (error && typeof error === 'object') {
            // Convert object to string for display
            errorDetail = JSON.stringify(error);
          }
        } catch (parseError) {
          console.log('❌ Failed to parse error response:', parseError);
        }
        setErrorMessage(errorDetail);
      }
    } catch (error) {
      console.log('💥 Exception caught:', error);
      setErrorMessage('Failed to update profile. Please try again.');
    } finally {
      setSaveLoading(false);
    }
  };

  const getRoleDisplay = (role: string) => {
    switch (role) {
      case 'superadmin':
        return { label: 'Super Administrator', color: 'error' as const, icon: <AdminPanelSettings /> };
      case 'doctor':
        return { label: 'Doctor', color: 'primary' as const, icon: <LocalHospital /> };
      case 'marketinguser':
        return { label: 'Marketing User', color: 'secondary' as const, icon: <Person /> };
      default:
        return { label: 'User', color: 'default' as const, icon: <Person /> };
    }
  };

  const roleInfo = getRoleDisplay(user.role);

  // Debug logging
  // console.log('🔍 Current user object:', user);
  // console.log('🖼️ Profile image value:', user?.profile_image);
  // console.log('🔧 Fixed profile image URL:', fixS3Url(user?.profile_image));
  // console.log('⏰ Image timestamp:', imageTimestamp);

  return (
    <Box sx={{ maxWidth: 800, margin: '0 auto', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Profile
      </Typography>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {errorMessage}
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: 4 }}>
          {/* Header Section */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
            <Box sx={{ position: 'relative', display: 'inline-block' }}>
              <Avatar
                key={user?.profile_image ? `${user.profile_image}-${imageTimestamp}` : 'no-image'}
                src={getCacheBustedImageUrl(user?.profile_image, imageTimestamp)}
                imgProps={{
                  crossOrigin: "anonymous",
                  onError: (e) => {
                    console.error('❌ Avatar image failed to load:', user?.profile_image);
                    console.error('Error event:', e);
                    console.error('This is likely a CORS or S3 permissions issue');
                    // Try to fetch the image to get more details
                    fetch(user?.profile_image || '', { mode: 'no-cors' })
                      .then(() => console.log('✅ Image exists but CORS is blocking it'))
                      .catch(err => console.error('❌ Image fetch error:', err));
                  }
                }}
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: user?.profile_image ? 'transparent' : 'primary.main',
                  fontSize: '2rem',
                  mr: 3,
                  border: user?.profile_image ? '2px solid #e0e0e0' : 'none'
                }}
              >
                {(!user?.profile_image && (user.full_name || user.username)?.charAt(0)?.toUpperCase()) || 'U'}
              </Avatar>
              {user.role === 'doctor' && !user.profile_image && (
                <Box sx={{
                  position: 'absolute',
                  bottom: -5,
                  right: -5,
                  bgcolor: 'error.main',
                  color: 'white',
                  borderRadius: '50%',
                  width: 20,
                  height: 20,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  border: '2px solid white'
                }} title="Profile picture required">
                  !
                </Box>
              )}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" gutterBottom>
                {user.full_name || user.username}
              </Typography>
              <Chip
                icon={roleInfo.icon}
                label={roleInfo.label}
                color={roleInfo.color}
                sx={{ mb: 1 }}
              />
              <Typography variant="body2" color="textSecondary">
                Member since {new Date(user.created_at || Date.now()).toLocaleDateString()}
              </Typography>
            </Box>
            {!isEditing && (
              <Button
                variant="contained"
                startIcon={<Edit />}
                onClick={handleEdit}
              >
                Edit Profile
              </Button>
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Profile Information */}
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            Profile Information
          </Typography>

          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
            gap: 3
          }}>
            <Box>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <Person />
                  </ListItemIcon>
                  <ListItemText
                    primary="Name"
                    secondary={
                      isEditing ? (
                        <TextField
                          value={editData.username}
                          onChange={(e) => handleFieldChange('username', e.target.value)}
                          size="small"
                          fullWidth
                          margin="dense"
                          error={!!formErrors.username}
                          helperText={formErrors.username || 'This will be used as both username and full name'}
                        />
                      ) : (
                        user.username || user.full_name
                      )
                    }
                  />
                </ListItem>
              </List>
            </Box>

            <Box>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <Email />
                  </ListItemIcon>
                  <ListItemText
                    primary="Email"
                    secondary={user.email}
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <Phone />
                  </ListItemIcon>
                  <ListItemText
                    primary="Phone Number"
                    secondary={
                      isEditing ? (
                        <TextField
                          value={editData.phoneNumber}
                          onChange={(e) => handleFieldChange('phoneNumber', e.target.value)}
                          size="small"
                          fullWidth
                          margin="dense"
                          placeholder="Enter phone number"
                          error={!!formErrors.phoneNumber}
                          helperText={formErrors.phoneNumber || 'Enter 10 digit Indian phone number (e.g., 9876543210)'}
                          inputProps={{ maxLength: 10 }}
                        />
                      ) : (
                        user?.phoneNumber || 'Not provided'
                      )
                    }
                  />
                </ListItem>
              </List>
            </Box>
          </Box>

          {/* Profile Picture Upload */}
          <ProfilePictureUpload
            profileImage={user.profile_image}
            onProfilePictureChange={handleProfilePictureChange}
            onProfilePictureDelete={handleDeleteProfilePicture}
            uploading={saveLoading}
            error={errorMessage}
            success={successMessage}
            size={120}
            label="Upload Profile Picture"
            description="Upload your profile picture. Maximum file size: 5MB. Supported formats: JPG, PNG, GIF."
            showDelete={true}
          />

          {/* Doctor-specific Information */}
          {user.role === 'doctor' && (
            <>
              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Professional Information
              </Typography>

              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                gap: 3
              }}>
                <Box>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <LocalHospital />
                      </ListItemIcon>
                      <ListItemText
                        primary="Specialization"
                        secondary={
                          isEditing ? (
                            <TextField
                              value={editData.specialization}
                              onChange={(e) => handleFieldChange('specialization', e.target.value)}
                              size="small"
                              fullWidth
                              margin="dense"
                              placeholder="e.g., Cardiologist"
                              error={!!formErrors.specialization}
                              helperText={formErrors.specialization || 'Enter your medical specialization'}
                            />
                          ) : (
                            user.specialization || 'N/A'
                          )
                        }
                      />
                    </ListItem>

                    <ListItem>
                      <ListItemIcon>
                        <Badge />
                      </ListItemIcon>
                      <ListItemText
                        primary="Department"
                        secondary={
                          isEditing ? (
                            <TextField
                              value={editData.department}
                              onChange={(e) => handleFieldChange('department', e.target.value)}
                              size="small"
                              fullWidth
                              margin="dense"
                              placeholder="e.g., Cardiology"
                              error={!!formErrors.department}
                              helperText={formErrors.department || 'Optional: Enter your department'}
                            />
                          ) : (
                            user.department || 'N/A'
                          )
                        }
                      />
                    </ListItem>

                    <ListItem>
                      <ListItemIcon>
                        <Badge />
                      </ListItemIcon>
                      <ListItemText
                        primary="Hospital Name"
                        secondary={
                          isEditing ? (
                            <TextField
                              value={editData.hospital_name}
                              onChange={(e) => handleFieldChange('hospital_name', e.target.value)}
                              size="small"
                              fullWidth
                              margin="dense"
                              placeholder="e.g., City General Hospital"
                              error={!!formErrors.hospital_name}
                              helperText={formErrors.hospital_name || 'Optional: Enter your hospital name'}
                            />
                          ) : (
                            user.hospital_name || 'N/A'
                          )
                        }
                      />
                    </ListItem>
                  </List>
                </Box>

                <Box>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <Badge />
                      </ListItemIcon>
                      <ListItemText
                        primary="License Number"
                        secondary={
                          isEditing ? (
                            <TextField
                              value={editData.license_number}
                              onChange={(e) => handleFieldChange('license_number', e.target.value)}
                              size="small"
                              fullWidth
                              margin="dense"
                              placeholder="Medical license number"
                              error={!!formErrors.license_number}
                              helperText={formErrors.license_number || 'Enter your medical license number'}
                            />
                          ) : (
                            user.license_number || 'N/A'
                          )
                        }
                      />
                    </ListItem>

                    <ListItem>
                      <ListItemIcon>
                        <DateRange />
                      </ListItemIcon>
                      <ListItemText
                        primary="Years of Experience"
                        secondary={
                          isEditing ? (
                            <TextField
                              value={editData.years_of_experience}
                              onChange={(e) => handleFieldChange('years_of_experience', e.target.value)}
                              size="small"
                              fullWidth
                              margin="dense"
                              type="number"
                              placeholder="Years"
                              error={!!formErrors.years_of_experience}
                              helperText={formErrors.years_of_experience || 'Enter years of experience (0-60)'}
                              inputProps={{ min: 0, max: 60 }}
                            />
                          ) : (
                            user.years_of_experience ? `${user.years_of_experience} years` : 'N/A'
                          )
                        }
                      />
                    </ListItem>

                    <ListItem>
                      <ListItemIcon>
                        <Person />
                      </ListItemIcon>
                      <ListItemText
                        primary="Location"
                        secondary={
                          isEditing ? (
                            <TextField
                              value={editData.location}
                              onChange={(e) => handleFieldChange('location', e.target.value)}
                              size="small"
                              fullWidth
                              margin="dense"
                              placeholder="e.g., New York, NY"
                              error={!!formErrors.location}
                              helperText={formErrors.location || 'Optional: Enter your location'}
                            />
                          ) : (
                            user.location || 'N/A'
                          )
                        }
                      />
                    </ListItem>
                  </List>
                </Box>
              </Box>
            </>
          )}

          {isEditing && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
              <Button
                variant="outlined"
                startIcon={<Cancel />}
                onClick={handleCancel}
                disabled={saveLoading}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={saveLoading ? <CircularProgress size={20} /> : <Save />}
                onClick={handleSave}
                disabled={saveLoading}
              >
                {saveLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Account Status Card */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Account Status
          </Typography>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
            gap: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="textSecondary">
                Status:
              </Typography>
              <Chip
                label={user.is_active ? 'Active' : 'Inactive'}
                color={user.is_active ? 'success' : 'error'}
                size="small"
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="textSecondary">
                Admin Privileges:
              </Typography>
              <Chip
                label={user.is_admin ? 'Yes' : 'No'}
                color={user.is_admin ? 'primary' : 'default'}
                size="small"
              />
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}